/**
 * Watermark Configuration Component
 * 
 * Allows users to configure brand watermark position, opacity, and size.
 */

import React from 'react';
import { WatermarkConfig as WatermarkConfigType } from '../../types/overlayConfig';

interface WatermarkConfigProps {
  config: WatermarkConfigType;
  onChange: (config: WatermarkConfigType) => void;
}

const WatermarkConfig: React.FC<WatermarkConfigProps> = React.memo(({
  config,
  onChange,
}) => {
  const positionOptions: Array<{ value: WatermarkConfigType['position']; label: string }> = [
    { value: 'bottom-right', label: 'Bottom Right' },
    { value: 'bottom-left', label: 'Bottom Left' },
    { value: 'bottom', label: 'Bottom (Centered)' },
    { value: 'top-right', label: 'Top Right' },
    { value: 'top-left', label: 'Top Left' },
    { value: 'top', label: 'Top (Centered)' },
  ];

  const sizeOptions: Array<{ value: WatermarkConfigType['size']; label: string }> = [
    { value: 'small', label: 'Small' },
    { value: 'medium', label: 'Medium' },
    { value: 'large', label: 'Large' },
  ];

  const logoTypeOptions: Array<{ value: 'mark' | 'full'; label: string }> = [
    { value: 'mark', label: 'Icon Only (Favicon)' },
    { value: 'full', label: 'Logo with Text' },
  ];

  // Handle config changes - do NOT populate logoData (worker uses logoType only)
  const handleChange = (updates: Partial<WatermarkConfigType>) => {
    const newConfig = { ...config, ...updates };
    
    // Remove logoData - worker uses logoType to fetch PNGs from fixed paths
    // logoData is only used for UI preview, not saved to database
    delete newConfig.logoData;
    
    onChange(newConfig);
  };

  return (
    <div className="space-y-3">
      {/* Logo Type Selection */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1.5">
          Logo Type
        </label>
        <select
          value={config.logoType || 'mark'}
          onChange={(e) => handleChange({
            logoType: e.target.value as 'mark' | 'full',
          })}
          className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          {logoTypeOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Logo Path (disabled for now - future feature) */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1.5">
          Logo Path
        </label>
        <input
          type="text"
          value={config.logoPath || ''}
          onChange={(e) => handleChange({
            logoPath: e.target.value || undefined,
          })}
          placeholder="brands/kridah-logo.png"
          disabled
          className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        />
        <p className="text-xs text-gray-500 mt-1">
          Custom logo path (future feature). Currently using Kridah Labs logo.
        </p>
      </div>

      {/* Position Selection */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1.5">
          Position
        </label>
        <select
          value={config.position}
          onChange={(e) => handleChange({
            position: e.target.value as WatermarkConfigType['position'],
          })}
          className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          {positionOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Opacity Slider */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1.5">
          Opacity: {Math.round(config.opacity * 100)}%
        </label>
        <input
          type="range"
          min="0"
          max="1"
          step="0.1"
          value={config.opacity}
          onChange={(e) => handleChange({
            opacity: parseFloat(e.target.value),
          })}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
        />
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>0%</span>
          <span>100%</span>
        </div>
      </div>

      {/* Size Selection */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1.5">
          Size
        </label>
        <select
          value={config.size}
          onChange={(e) => handleChange({
            size: e.target.value as WatermarkConfigType['size'],
          })}
          className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          {sizeOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
});

WatermarkConfig.displayName = 'WatermarkConfig';

export default WatermarkConfig;
